import { createTheme } from '@mui/material/styles';
import '@mui/x-data-grid/themeAugmentation';
import { ColorPartial } from '@mui/material/styles/createPalette';
import { NoRowsOverlay } from '@shared/components/grid/overlays/NoRowsOverlay';
import { LoadingOverlay } from '@shared/components/grid/overlays/LoadingOverlay';
import { NoResultsOverlay } from '@shared/components/grid/overlays/NoResultsOverlay';


declare module '@mui/material/styles' {
	interface Palette {
		yellow: Palette['primary'];
		orange: ColorPartial;
	}

	interface PaletteOptions {
		yellow?: PaletteOptions['primary'];
		orange?: ColorPartial;
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides {
		yellow: true;
	}
}
export const extraColor = {
	palette: {
		green: {
			'main': '#1a551f',
			'light': '#e5f0da',
			'dark': '#c1eb98'
		},
		error: {
			'primary': '#f88a86',
			'secondary': '#f44336cf',
			'light': '#e42424'
		},
		blue: {
			'light': '#e9f2f7',
			'main': '#a3d0e4',
			'500':'#0089fe'
		
		},
		grey: {
			'light': '#efefef',
			'main': '#d8d8d8',
			'dark': '#8484841c',
			'400': '#0000004d',
			'offwhite':'#e9e8ee'
		},
		transparent: {
			'light': '#6d6d6d00',
		},
		orange: {
			'dark': '#dc6601',
			'main': '#f1b047',
			'light': '#f5c77e',
			'400': '#ffc107',
		}
	}
};

export const colorTheme = createTheme({
	palette: {
		mode: 'light',
		primary: {
			main: '#0B5179',    // `${colorTheme.palette.primary.main}`
			light: '#3684B0',   // `${colorTheme.palette.primary.light}`
			dark: '#053D5D',    // colorTheme.palette.primary.dark
		},
		secondary: {
			main: '#C3C3C3',    // `${colorTheme.palette.secondary.main}`
			light: '#F2F2F2',   // `${colorTheme.palette.secondary.light}`
			dark: '#7B838A',    // colorTheme.palette.secondary.dark
		},
		info: {
			main: '#3684B0',    // colorTheme.palette.info.main
			light: '#FFBD54',   // `${colorTheme.palette.info.light}`
			dark: '#E26943',    // `${colorTheme.palette.info.dark}`
		},
		text: {
			primary: '#000000',     // `${colorTheme.palette.text.primary}`
			secondary: '#2D2D2D',   // colorTheme.palette.text.secondary
		},
		error: {
			main: '#d32f2f',            // `${colorTheme.palette.error.main}`
			light: '#f4b8b6',           // `${colorTheme.palette.error.light}`
			contrastText: '#e0433d',    // `${colorTheme.palette.error.contrastText}`
		},
		common: {
			white: '#fff',      //  `${colorTheme.palette.common.white}`
			black: '#000',      // colorTheme.palette.common.black
		},
		success: {
			main: '#67a12f',    // colorTheme.palette.success.main
			dark: '#578528',    // colorTheme.palette.success.dark
			light: '#e5f0da',
			'600': '#1a551f',    // colorTheme.palette.success[600]
		},
		grey: {
			'400': '#e1e1e1',   // colorTheme.palette.grey.400
			'500': '#dddddd',   // colorTheme.palette.grey.500
			'700': '#949494'    // colorTheme.palette.grey.700
		},
		yellow: {
			main: '#fabb65',    // colorTheme.palette.yellow.main
		},
		orange: {
			'50': '#FFF5E6',    // colorTheme.palette.orange[50]
			'200': '#FF851B'    // colorTheme.palette.orange[200]
		}
		
	},
	spacing: 8,
	drawerWidth: '260px',
	typography: {
		fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
	},
});

export const muiTheme = createTheme(colorTheme, {
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					fontSize: '13px',
					lineHeight: '1.3',
					'@page': {
						size: 'auto',
						marginTop: '0.5cm',
					},
				},
				span: {
					fontSize: '13px',
					lineHeight: '1.3',
				},
				div: {
					fontSize: '13px',
					lineHeight: '1.3',
				}
			}
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					gap: '10px',
					display: 'flex',
					alignItems: 'center',
					fontSize: 12,
				},
				h1: {
					fontSize: 22,
					fontWeight: 500,
					lineHeight: '20px',
				},
				h2: {
					fontSize: 20,
					fontWeight: 400,
					lineHeight: 1.3,
				},
				h3: {
					fontSize: 18,
					fontWeight: 400,
					marginBottom: '10px',
					lineHeight: 1.3,
				},
				h4: {
					fontSize: 16,
					fontWeight: 400,
				},
				body1: {
					fontSize: 13,
					fontWeight: 400,
				},
				subtitle2: {},
				subtitle1: {
					fontSize: 14,
					fontWeight: 400,
				},
				caption: {
					color: `${colorTheme.palette.common.black}`,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: 700,
					minHeight: '30px',
					boxShadow: 'none',
				},
				containedPrimary: {
					backgroundColor: `${colorTheme.palette.primary.light}`,
				},
				containedError: {
					color: `${colorTheme.palette.common.white}`
				},
				containedInfo: {
					color: `${colorTheme.palette.common.white}`,
					background:  `${colorTheme.palette.info.dark}`,
					'&:hover': {
						backgroundColor:  `${colorTheme.palette.info.dark}`
					}
				},
				textInfo: {
					color:  `${colorTheme.palette.info.dark}`
				}
			},
		},
		MuiSelect: {
			styleOverrides: {
				select: {
					fontSize: 12,
					paddingTop: 10,
					paddingBottom: 0,
					fontWeight: 600,
					color: `${colorTheme.palette.primary.main}`,
					borderRadius: 0
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					color: `${colorTheme.palette.secondary.main}`,
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					letterSpacing: '.445px',
					textTransform: 'none',
					'&.Mui-selected': {
						fontWeight: 700,
						letterSpacing: 0,
					},
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					border: 'none',
				},
				indicator: {
					height: '4px',
					backgroundColor:  `${colorTheme.palette.info.light}`,
					transition: 'all 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
				},
				scrollButtons: {
					'&.Mui-disabled': {
						display: 'none',
					},
				},
			},
		},
		MuiDataGrid: {
			defaultProps: {
				editMode: 'row',
				pagination: true,
				components: {
					NoRowsOverlay,
					NoResultsOverlay,
					LoadingOverlay,
				},
			},
			styleOverrides: {
				root: {
					border: 'none',
					overflow: 'hidden',
				},
				filterForm: {
					gap: 20,
					padding: 20,
					paddingRight: 10,
				},
				filterFormDeleteIcon: {
					order: 1,
					marginLeft: -10,
				},
				row: {
					fontSize: 13,
					cursor: 'pointer',
					'&--editing': {
						boxShadow: 'none',
						'.MuiDataGrid-cell': {
							background: 'transparent',
							minHeight: 38
						},
					},
					'&.Mui-selected, &:hover': {
						'.MuiDataGrid-cell[data-field="options"] > div, .MuiDataGrid-cell:last-child > *': {
							opacity: 1,
							pointerEvents: 'auto',
						},
					},
					'.Mui-selected': {
						background: `${colorTheme.palette.primary.light}`,
					},
				},
				cell: {
					color: `${colorTheme.palette.common.black}`,
					borderColor: `${colorTheme.palette.secondary.main}`,
					'&:focus, &:active, &:focus-within': {
						outline: 'none',
					},
					'&:first-child': {
						paddingLeft: 8,
						'&:not(.MuiDataGrid-cellCheckbox)': {
							padding: '8px 10px 8px 20px',
						},
					},
					'&.MuiDataGrid-cell--editing': {
						boxShadow: 'none',
						padding: '0px 10px 0px 10px',
						background: 'none',
						'.MuiInputBase-root': {
							height: '100%',
							borderRadius: 0
						},
						'.MuiOutlinedInput-root.Mui-error': {
							borderBottom: `1px solid ${colorTheme.palette.error.main}`
						},
						input: {
							textAlign: 'inherit',
							borderRadius: 0,
							border: 'none',
							'&:focus': {
								borderColor: `${colorTheme.palette.primary.main}`,
							},
							'&[type="datetime-local"], &[type="date"]': {
								padding: '4px 4px 4px 10px',
							},
						},
						'&:focus-within': {
							outline: 'none',
						},
						'&:last-child': {
							minWidth: 140,
						},
					},
				},
				editInputCell: {
					fontSize: 'inherit',
					input: {
						padding: '0 10px',
					},
				},
				columnHeaders: {
					backgroundColor: `${colorTheme.palette.secondary.light}`,
				},
				columnHeader: {
					padding: '0 8px',
					'&--sortable': {
						'&:first-child': {
							padding: '0 20px',
						},
					},
					'&:focus-within': {
						outline: 'none',
					},
					'&:last-child': {
						'.MuiDataGrid-columnSeparator': {
							display: 'none',
						},
					},
				},
				columnHeaderTitle: {
					fontWeight: 700,
					lineHeight: '19px',
					whiteSpace: 'normal',
					color: `${colorTheme.palette.common.black}`,
				},
				columnHeaderTitleContainer: {
					maxWidth: '100%',
				},
				checkboxInput: {
					color: `${colorTheme.palette.secondary.main}`,
					'&.Mui-checked': {
						color: `${colorTheme.palette.primary.main}`,
					},
				},
			},
		},
		MuiPopover: {
			defaultProps: {
				elevation: 3,
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					overflow: 'hidden',
					fontSize: 12,
					paddingTop: 0,
					paddingBottom: 0,
					fontWeight: 400,
					border: 0,
					borderBottom: `1px solid ${colorTheme.palette.secondary.main}`,
					borderRadius: 0,
					'&.Mui-disabled': {
						background: `${colorTheme.palette.secondary.main}`,
					},
				},
				input: {
					background: `${colorTheme.palette.common.white}`,
					color: `${colorTheme.palette.primary.main}`,
					borderRadius: 0,
					'&.Mui-disabled': {
						background: 'transparent',
						'-webkit-text-fill-color': `${colorTheme.palette.primary.main}`,
					},
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					bottom: 0,
					margin: 0,
					position: 'absolute',
					transform: 'translateY(100%)',
				},
			},
		},
		MuiGrid: {
			styleOverrides: {
				root: {
					'&.MuiGrid-container': {
						marginBottom: 10
					}
				}
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					height: '100%',
				},
				label: {
					fontWeight: 100,
				},
			},
		},
		MuiLinearProgress: {
			styleOverrides: {
				root: {
					height: '2px',
				},
			},
		},
	},
});
